import { type IAdvancedRichText } from '@framework/engine/inputs/rich-text-advanced/IAdvancedRichText.ts';
import { replaceRichTextReferences } from '@framework/engine/inputs/rich-text-advanced/render/postprocess-html/replaceRichTextReferences.tsx';
import { isRichTextSchema } from '@framework/engine/inputs/rich-text-advanced/schema/RichTextAdvancedMeta.ts';
import { MigrationFunction } from '@framework/engine/migrations/MigrationFunction.ts';
import { type ComputeExpression } from '@framework/engine/schema/expressions/compute/ComputeExpression.ts';
import { encodeDataReference } from '@framework/engine/schema/expressions/compute/input/nodes/ComputeExpressionReferenceNode.tsx';
import { type IReference } from '@framework/engine/schema/expressions/references/IReference.ts';
import { isReferenceSchema } from '@framework/engine/schema/expressions/validation/isReferenceSchema.ts';
import { type IRootWidget } from '@framework/engine/schema/widgets/repository/containers/root/IRootWidget.ts';
import { RootWidgetSchema } from '@framework/engine/validator/root/RootWidgetSchema.ts';
import { forEachSchemaNode } from '@framework/validation/describe/forEachSchemaNode.ts';
import { getApplicantOrUserReferences } from '../applicant-or-user/getApplicantOrUserReferences.ts';
import { getSessionDataReferences } from '../session/getSessionDataReferences.ts';

/**
 * Rinomina le reference al richiedente in "Utente Attivo" (FWP-4148):
 * - `Richiedente: <...>` → `Utente Attivo: <...>`;
 * - `Genere` → `Utente Attivo: Genere`;
 * - `Titolare o Richiedente: <...>` → `Titolare o Utente Attivo: <...>`.
 *
 * Le label sono salvate insieme alla reference nella configurazione della
 * sezione: vengono riallineate, insieme al `group`, alle definizioni correnti
 * (`getSessionDataReferences`, `getApplicantOrUserReferences`).
 * `id`, `code` e `path` non cambiano: la risoluzione dei valori è invariata.
 *
 * L'albero viene visitato tramite lo schema, come in `collectSchemaReferences`.
 */
export class ReferenceMigration002ActiveUserLabels extends MigrationFunction<IRootWidget> {
  readonly versionId = 2;

  /** Definizioni correnti delle reference, indicizzate per `id` */
  private readonly currentReferences = new Map(
    [...getSessionDataReferences(), ...getApplicantOrUserReferences()].map(
      reference => [reference.id, reference],
    ),
  );

  run(data: IRootWidget): void {
    forEachSchemaNode(RootWidgetSchema, { value: data }, node => {
      if (node.value == null) return;

      // Reference "normale" (default, regole, condizioni, espressioni di calcolo)
      if (isReferenceSchema(node.schema)) {
        this.migrateReference(node.value);
      }

      // Reference serializzate nel rich text
      if (isRichTextSchema(node.schema)) {
        this.migrateRichText(node.value);
      }

      // Espressione di calcolo
      if (
        typeof node.value.raw === 'string' &&
        Array.isArray(node.value.value)
      ) {
        this.migrateComputeExpression(node.value);
      }
    });
  }

  private findCurrentReference(
    reference: IReference | null | undefined,
  ): IReference | undefined {
    if (!reference) return undefined;
    const current = this.currentReferences.get(reference.id);
    return current?.type === reference.type ? current : undefined;
  }

  /**
   * Allinea `label` e `group` della reference alla definizione corrente.
   *
   * @returns `true` se la reference è stata modificata.
   */
  private migrateReference(reference: IReference): boolean {
    const current = this.findCurrentReference(reference);
    if (
      !current ||
      (reference.label === current.label && reference.group === current.group)
    ) {
      return false;
    }

    this.log('Reference rinominata', [reference.label, current.label]);
    reference.label = current.label;
    reference.group = current.group;
    return true;
  }

  /**
   * Nel rich text la label è ripetuta, oltre che nella reference serializzata
   * (`data-reference`), anche in `data-label` e nel testo del tag.
   * I tag non decodificabili restano invariati: il risultato viene salvato.
   */
  private migrateRichText(richText: IAdvancedRichText): void {
    for (const item of richText) {
      const text = replaceRichTextReferences(
        item.text,
        (reference, encoded, match) => {
          const oldLabel = reference.label;
          if (!this.migrateReference(reference)) return match;

          return match
            .replace(encoded, encodeDataReference(reference))
            .replace(
              `data-label="${oldLabel}"`,
              `data-label="${reference.label}"`,
            )
            .replace(`>@${oldLabel}<`, `>@${reference.label}<`);
        },
        { keepInvalidReferences: true },
      );
      if (text != null) item.text = text;
    }
  }

  /**
   * Nelle espressioni di calcolo la label è ripetuta negli `attrs` del nodo e
   * nel testo `raw` (`@<label>`). La reference in `attrs.reference` viene
   * invece migrata quando la visita raggiunge il suo nodo.
   */
  private migrateComputeExpression(expression: ComputeExpression): void {
    for (const item of expression.value) {
      // `attrs` è opzionale nello schema: i nodi incompleti vengono ignorati
      if (item.type !== 'compute-expression-reference' || !item.attrs) continue;

      const { attrs } = item;
      const current = this.findCurrentReference(attrs.reference);
      if (!current || attrs.label === current.label) continue;

      expression.raw = expression.raw.replaceAll(
        `@${attrs.label}`,
        `@${current.label}`,
      );
      attrs.label = current.label;
    }
  }
}
